import { Fragment, useCallback, useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { getFiscalYear } from '../../api/fiscalYears'
import { listEligibilityResults, listJudgmentGates } from '../../api/eligibility'
import { ApiError } from '../../api/problem'
import { useProgramRefs } from '../../lib/useProgramRefs'
import type { EligibilityResultView, JudgmentGateView } from '../../types/eligibility'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'

export interface EligibilityPanelProps {
  filings: ProgramFilingDetailView[]
}

interface EligibilityRow {
  filing: ProgramFilingDetailView
  result: EligibilityResultView
}

interface GateRow {
  filing: ProgramFilingDetailView
  gate: JudgmentGateView
}

const VERDICT_VARIANT: Record<string, StatusPillVariant> = {
  pass: 'success',
  fail: 'danger',
  needs_review: 'warning',
}

const VERDICT_LABEL: Record<string, string> = {
  pass: 'Pass',
  fail: 'Fail',
  needs_review: 'Needs review',
}

const GATE_STATE_VARIANT: Record<string, StatusPillVariant> = {
  true: 'success',
  false: 'danger',
  unresolved: 'warning',
}

/**
 * The Case-workspace Eligibility tab (IB-11, GA demo lifecycle-tabs pass) — the `EligibilityResult`
 * verdicts (`GET .../eligibility-results`) and authored `JudgmentGate`s (`GET .../judgment-gates`)
 * for each of this case's `ProgramFiling`s. Both reads were already published on the wire before
 * this story (`EligibilityEvaluationController.listResults`/`listJudgmentGates`) — no additive
 * backend route needed. A filing whose subjects have never been evaluated, or whose `Program` has
 * no resolvable `EligibilityModel` as-of today, contributes no rows for that half of the tab — an
 * honest, named empty state (per section), never a fabricated verdict or gate.
 *
 * <b>Disclosed gap, not fabricated.</b> `JudgmentAssertion` (the human sign-off record behind a
 * gate's `true`/`false`/`unresolved` state, FR-M8.3) has no `GET` anywhere in `module.eligibility`
 * — only `POST /judgment-gates/{id}/assertions` exists (docs/api-spec.md §10's own status note).
 * The per-subject gate *state* is not silently guessed at here: it is shown exactly where the
 * backend already surfaces it — inside each `EligibilityResult`'s own `reasoningTrace.gateStates`
 * (the aggregation's own record of what state it read) — while the `JudgmentGate`s section below
 * shows only the authored prompt/guidance/rubric, never an assertion history this screen cannot see.
 */
export function EligibilityPanel({ filings }: EligibilityPanelProps) {
  const [resultRows, setResultRows] = useState<EligibilityRow[]>([])
  const [gateRows, setGateRows] = useState<GateRow[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [fiscalYearLabelById, setFiscalYearLabelById] = useState<Map<string, string>>(new Map())
  const programRefs = useProgramRefs(filings.map((f) => f.programId))

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    Promise.all([
      Promise.all(
        filings.map((filing) =>
          listEligibilityResults(filing.id).then((results) => results.map((result) => ({ filing, result }))),
        ),
      ),
      Promise.all(
        filings.map((filing) => listJudgmentGates(filing.id).then((gates) => gates.map((gate) => ({ filing, gate })))),
      ),
    ])
      .then(([resultLists, gateLists]) => {
        const flatResults = resultLists.flat()
        setResultRows(flatResults)
        setGateRows(gateLists.flat())
        setState('ready')
        const distinctFyIds = Array.from(new Set(flatResults.map((r) => r.result.fiscalYearId).filter(Boolean)))
        Promise.all(distinctFyIds.map((fyId) => getFiscalYear(fyId).catch(() => null))).then((years) => {
          setFiscalYearLabelById(
            new Map(years.filter((y): y is NonNullable<typeof y> => y !== null).map((y) => [y.id, y.label])),
          )
        })
      })
      .catch((err) => {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load eligibility data.')
        setState('error')
      })
  }, [filings])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading eligibility results…</p>
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-5)' }}>
      <section>
        <h3 style={{ margin: '0 0 var(--iw-space-2)', fontSize: 'var(--iw-size-md)' }}>Eligibility results</h3>
        {resultRows.length === 0 ? (
          <p style={{ color: 'var(--iw-text-muted)' }}>No subject on this case has been evaluated yet.</p>
        ) : (
          <table className="iw-table" aria-label="Eligibility results">
            <thead>
              <tr>
                <th scope="col">Program</th>
                <th scope="col">Fiscal year</th>
                <th scope="col">Subject</th>
                <th scope="col">Verdict</th>
                <th scope="col">Evidence gaps</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              {resultRows.map(({ filing, result }) => {
                const expanded = expandedId === result.id
                const gapCount = result.evidenceGaps?.length ?? 0
                return (
                  <Fragment key={result.id}>
                    <tr>
                      <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                        <EntityRef kind="program" id={filing.programId} name={programRefs.get(filing.programId)?.display} />
                      </td>
                      <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                        <EntityRef kind="fiscalYear" id={result.fiscalYearId} name={fiscalYearLabelById.get(result.fiscalYearId)} />
                      </td>
                      <td style={{ fontSize: 'var(--iw-size-xs)' }}>
                        {result.subjectType}{' '}
                        <span style={{ color: 'var(--iw-text-muted)' }}>{result.subjectId.slice(0, 8)}</span>
                      </td>
                      <td>
                        <StatusPill
                          variant={VERDICT_VARIANT[result.verdict] ?? 'neutral'}
                          label={VERDICT_LABEL[result.verdict] ?? result.verdict}
                        />
                      </td>
                      <td>{gapCount > 0 ? gapCount : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="iw-btn iw-btn--sm"
                          aria-expanded={expanded}
                          aria-label={`Reasoning trace for ${result.subjectType} ${result.subjectId}`}
                          onClick={() => setExpandedId(expanded ? null : result.id)}
                        >
                          {expanded ? 'Hide trace' : 'Reasoning trace'}
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                            <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                              Model {result.reasoningTrace.modelId} v{result.reasoningTrace.modelVersion}
                            </p>
                            <p style={{ margin: 0 }}>{result.reasoningTrace.summary}</p>
                            {result.reasoningTrace.gateStates.length > 0 ? (
                              <div>
                                <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Gate states</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--iw-space-2)' }}>
                                  {result.reasoningTrace.gateStates.map((gate, index) => (
                                    <StatusPill
                                      key={`${result.id}-gate-${index}`}
                                      variant={GATE_STATE_VARIANT[gate.state] ?? 'neutral'}
                                      label={`${gate.decisionKey}: ${gate.state}`}
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {result.reasoningTrace.firedRules.length > 0 ? (
                              <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                                {result.reasoningTrace.firedRules.length} fired rule(s)
                                {result.reasoningTrace.citations.length > 0
                                  ? ` · citations: ${result.reasoningTrace.citations.join(', ')}`
                                  : ''}
                              </p>
                            ) : null}
                            {gapCount > 0 ? (
                              <ul style={{ margin: 0, paddingLeft: 'var(--iw-space-4)' }}>
                                {(result.evidenceGaps ?? []).map((gap, index) => (
                                  <li
                                    key={`${result.id}-gap-${index}`}
                                    style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}
                                  >
                                    {gap}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3 style={{ margin: '0 0 var(--iw-space-2)', fontSize: 'var(--iw-size-md)' }}>Judgment gates</h3>
        {gateRows.length === 0 ? (
          <p style={{ color: 'var(--iw-text-muted)' }}>
            No judgment gate is authored for a resolvable EligibilityModel on this case&apos;s program(s) yet.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
            {gateRows.map(({ filing, gate }) => (
              <li
                key={gate.id}
                style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius)', padding: 'var(--iw-space-3)' }}
              >
                <p style={{ margin: 0, fontWeight: 'var(--iw-weight-medium)' as never }}>{gate.prompt}</p>
                <p style={{ margin: '4px 0 0', color: 'var(--iw-text-muted)' }}>{gate.guidance}</p>
                <p style={{ margin: '4px 0 0', color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)', display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
                  <EntityRef kind="program" id={filing.programId} name={programRefs.get(filing.programId)?.display} />
                  {gate.evidenceRequirementRef ? (
                    <span>
                      Evidence requirement: <EntityRef kind="record" id={gate.evidenceRequirementRef} />
                    </span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
